import React, { useEffect, useState } from "react";
import { ScrollNavbar } from "../../components";
import Footer from "../../components/Footer/Footer";
import Banner from "../../components/Landing/Banner";
import FirstImage from "../../assets/images/services/first.png";
import SecondImage from "../../assets/images/services/second.png";
import ThirdImage from "../../assets/images/services/third.png";
import "aos/dist/aos.css";
import AOS from "aos";

const Services = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  const [activeService, setActiveService] = useState(1);

  const services = [
    {
      id: 1,
      title: "Intercity Travel",
      icon: "🚗",
      image: FirstImage,
      color: "bg-gradient-to-br from-blue-500 to-blue-700",
      features: [
        {
          name: "Oneway Trips",
          description:
            "Travel comfortably between cities with our one-way service. Ideal for quick and hassle-free transfers without needing to return.",
        },
        {
          name: "Roundtrip Journeys",
          description:
            "Enjoy roundtrip journeys with professional drivers ensuring smooth rides to your destination and back.",
        },
      ],
    },
    {
      id: 2,
      title: "Airport Transfers",
      icon: "✈️",
      image: SecondImage,
      color: "bg-gradient-to-br from-green-500 to-green-700",
      features: [
        {
          name: "Airport Pickup",
          description:
            "Punctual and convenient airport pickup service with luggage assistance and swift journey to your location.",
        },
        {
          name: "Airport Drop Off",
          description:
            "Comfortable and timely airport drop-off service ensuring you reach the airport relaxed and on time.",
        },
      ],
    },
    {
      id: 3,
      title: "Hourly Rentals",
      icon: "⏰",
      image: ThirdImage,
      color: "bg-gradient-to-br from-purple-500 to-purple-700",
      features: [
        {
          name: "4 hours / 40 kilometers",
          description:
            "Perfect for short city trips, shopping, or meetings. Enjoy chauffeur-driven convenience for up to 4 hours.",
        },
        {
          name: "8 hours / 80 kilometers",
          description:
            "Suitable for day-long business meetings, city tours, or personal errands with full-day flexibility.",
        },
        {
          name: "12 hours / 120 kilometers",
          description:
            "Ideal for extended periods, full day sightseeing, or multiple meetings with maximum convenience.",
        },
      ],
    },
  ];

  const benefits = [
    {
      icon: "🛡️",
      title: "Reliable Service",
      desc: "Professional drivers and well-maintained vehicles",
    },
    {
      icon: "📱",
      title: "Tech-Driven",
      desc: "Seamless booking through our advanced platform",
    },
    {
      icon: "💰",
      title: "Transparent Pricing",
      desc: "No hidden charges, clear upfront pricing",
    },
    {
      icon: "⏱️",
      title: "24/7 Support",
      desc: "Round-the-clock customer assistance",
    },
  ];

  return (
    <>
      <ScrollNavbar />
      <div
        className="px-4 sm:px-8 lg:px-12"
        style={{ maxWidth: "1400px", margin: "0 auto" }}
      >
        {/* Hero Section */}
        <section className="my-20">
          <div className="w-full text-center">
            <h1
              className="text-4xl md:text-6xl font-mazzardbold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-black to-blue-900"
              data-aos="fade-up"
            >
              Services Offered
            </h1>
            <p
              className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Comprehensive chauffeur-driven cab rental services designed to
              meet your diverse travel needs
            </p>
          </div>
        </section>

        {/* Services Introduction */}
        <section className="my-20">
          <div className="w-full h-full flex box-border p-8 py-16 bg-gray-50 rounded-3xl">
            <div className="w-full h-full flex flex-col items-center justify-center">
              <div className="w-full text-center mb-8">
                <h2
                  className="text-3xl md:text-4xl font-mazzardbold bg-clip-text text-transparent bg-gradient-to-r from-black to-blue-900"
                  data-aos="fade-up"
                >
                  Premium Transportation Solutions
                </h2>
              </div>
              <p
                className="text-lg text-gray-700 leading-relaxed text-center max-w-4xl mx-auto"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                At Fusion Cab, we offer a range of chauffeur-driven cab rental
                services designed to meet the diverse needs of our customers.
                Whether you are traveling for business, leisure, or a special
                occasion, our services are tailored to provide a comfortable,
                reliable, and seamless travel experience.
              </p>
            </div>
          </div>
        </section>

        {/* Interactive Services Section */}
        <section className="my-20">
          <div
            className="flex justify-center items-center my-8 md:flex-row flex-col gap-4"
            data-aos="fade-up"
          >
            {services.map((service) => (
              <button
                key={service.id}
                className={`${
                  activeService === service.id
                    ? "bg-black text-white shadow-lg"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                } py-3 px-8 rounded-full text-lg font-medium transition-all duration-300`}
                onClick={() => setActiveService(service.id)}
              >
                {service.icon} {service.title}
              </button>
            ))}
          </div>

          {/* Service Details */}
          <div className="w-full flex justify-center items-center">
            {services.map(
              (service) =>
                activeService === service.id && (
                  <div key={service.id} className="w-full">
                    <div className="w-full h-full box-border p-8 flex justify-center items-center md:flex-row flex-col relative rounded-3xl my-8">
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-blue-50/20 rounded-3xl z-0"></div>

                      <div
                        className="md:w-2/5 w-full flex justify-center items-center h-full z-10 relative"
                        data-aos="fade-right"
                      >
                        <div className="h-5/6 relative">
                          <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-blue-100/50 filter blur-xl"></div>
                          <img
                            alt={service.title}
                            src={service.image}
                            className="w-[90%] drop-shadow-2xl rounded-lg relative"
                            style={{ maxHeight: "400px" }}
                          />
                          <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-full bg-blue-100/70 filter blur-xl"></div>
                        </div>
                      </div>

                      <div className="md:w-3/5 w-full z-10 relative">
                        <div className="w-full flex flex-col gap-4">
                          <div
                            className="font-mazzardbold text-3xl md:text-4xl my-3 bg-clip-text text-transparent bg-gradient-to-r from-black to-blue-900"
                            data-aos="fade-left"
                          >
                            {service.title}
                          </div>
                          <div className="my-2 text-left flex flex-col gap-4">
                            {service.features.map((feature, index) => (
                              <div
                                key={index}
                                data-aos="fade-left"
                                data-aos-delay={400 + index * 200}
                                className="bg-white rounded-lg p-4 shadow-sm"
                              >
                                <h4 className="font-mazzardbold text-lg text-gray-900 mb-2">
                                  {feature.name}
                                </h4>
                                <p className="text-gray-700 leading-relaxed">
                                  {feature.description}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
            )}
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="my-20">
          <div className="w-full h-full flex box-border p-8 py-16 bg-gray-50 rounded-3xl">
            <div className="w-full h-full flex flex-col items-center justify-center">
              <div className="w-full text-center mb-12">
                <h2
                  className="text-3xl md:text-4xl font-mazzardbold bg-clip-text text-transparent bg-gradient-to-r from-black to-blue-900"
                  data-aos="fade-up"
                >
                  Why Choose Fusion Cab?
                </h2>
                <p
                  className="text-gray-600 mt-4 max-w-2xl mx-auto"
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  Experience excellence in every journey with our premium
                  features
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="text-center group"
                    data-aos="fade-up"
                    data-aos-delay={300 + index * 100}
                  >
                    <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                      {benefit.icon}
                    </div>
                    <h3 className="text-lg font-mazzardbold text-gray-900 mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {benefit.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Banner Section */}
        <section className="my-20">
          <Banner />
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Services;
